import React from 'react';
import { Dialog, Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import {
  Classification,
  ClassificationGroup,
  ClassificationLevel,
} from '@core/generated/gql-types';
import {
  actionListboxButtonMdClassName,
  actionListboxOptionActiveClassName,
  actionPrimaryButtonClassName,
  actionTextClassName,
} from '@core/theme/actionTheme.util';
import { ModalState } from './types';

interface ClassificationLevelFormModalProps {
  isOpen: boolean;
  isPersisting: boolean;
  isLoadingGroups: boolean;
  isEditing: boolean;
  modalState: ModalState;
  sortedClassificationGroups: ClassificationGroup[];
  availableClassifications: Classification[];
  availableLevels: ClassificationLevel[];
  onClose: () => void;
  onGroupChange: (group: ClassificationGroup) => void;
  onClassificationChange: (classification: Classification) => void;
  onLevelChange: (level: ClassificationLevel) => void;
  onConfirm: () => void;
}

const ClassificationLevelFormModal: React.FC<
  ClassificationLevelFormModalProps
> = ({
  isOpen,
  isPersisting,
  isLoadingGroups,
  isEditing,
  modalState,
  sortedClassificationGroups,
  availableClassifications,
  availableLevels,
  onClose,
  onGroupChange,
  onClassificationChange,
  onLevelChange,
  onConfirm,
}) => {
  return (
    <Transition appear show={isOpen} as={React.Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => {
          if (!isPersisting) onClose();
        }}
      >
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg leading-6 font-medium text-gray-900"
                >
                  {isEditing
                    ? 'Edit Classification Level'
                    : 'Add Classification Level'}
                </Dialog.Title>

                <div className="mt-4 space-y-4">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Industry
                    </label>
                    <Listbox
                      value={modalState.classificationGroup}
                      onChange={onGroupChange}
                      disabled={isLoadingGroups || isPersisting}
                    >
                      <div className="relative">
                        <Listbox.Button
                          className={`${actionListboxButtonMdClassName} disabled:bg-gray-100`}
                        >
                          <span
                            className={`block truncate ${!modalState.classificationGroup ? 'text-gray-400' : ''}`}
                          >
                            {modalState.classificationGroup?.name ||
                              'Select industry'}
                          </span>
                          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                          </span>
                        </Listbox.Button>
                        <Transition
                          as={React.Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                        >
                          <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none">
                            {sortedClassificationGroups.map((group) => (
                              <Listbox.Option
                                key={group.id}
                                value={group}
                                className={({ active }) =>
                                  `relative cursor-default py-2 pr-4 pl-10 select-none ${
                                    active
                                      ? actionListboxOptionActiveClassName
                                      : 'text-gray-900'
                                  }`
                                }
                              >
                                {({ selected }) => (
                                  <>
                                    <span
                                      className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}
                                    >
                                      {group.name}
                                    </span>
                                    {selected && (
                                      <span
                                        className={`absolute inset-y-0 left-0 flex items-center pl-3 ${actionTextClassName}`}
                                      >
                                        <CheckIcon className="h-5 w-5" />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </Transition>
                      </div>
                    </Listbox>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Skill Set
                    </label>
                    <Listbox
                      value={modalState.classification}
                      onChange={onClassificationChange}
                      disabled={!modalState.classificationGroup || isPersisting}
                    >
                      <div className="relative">
                        <Listbox.Button
                          className={`${actionListboxButtonMdClassName} disabled:bg-gray-100`}
                        >
                          <span
                            className={`block truncate ${!modalState.classification ? 'text-gray-400' : ''}`}
                          >
                            {modalState.classification?.name ||
                              'Select skill set'}
                          </span>
                          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                          </span>
                        </Listbox.Button>
                        <Transition
                          as={React.Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                        >
                          <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none">
                            {availableClassifications.map((classification) => (
                              <Listbox.Option
                                key={classification?.id}
                                value={classification}
                                className={({ active }) =>
                                  `relative cursor-default py-2 pr-4 pl-10 select-none ${
                                    active
                                      ? actionListboxOptionActiveClassName
                                      : 'text-gray-900'
                                  }`
                                }
                              >
                                {({ selected }) => (
                                  <>
                                    <span
                                      className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}
                                    >
                                      {classification?.name}
                                    </span>
                                    {selected && (
                                      <span
                                        className={`absolute inset-y-0 left-0 flex items-center pl-3 ${actionTextClassName}`}
                                      >
                                        <CheckIcon className="h-5 w-5" />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </Transition>
                      </div>
                    </Listbox>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Level
                    </label>
                    <Listbox
                      value={modalState.classificationLevel}
                      onChange={onLevelChange}
                      disabled={!modalState.classification || isPersisting}
                    >
                      <div className="relative">
                        <Listbox.Button
                          className={`${actionListboxButtonMdClassName} disabled:bg-gray-100`}
                        >
                          <span
                            className={`block truncate ${!modalState.classificationLevel ? 'text-gray-400' : ''}`}
                          >
                            {modalState.classificationLevel?.name ||
                              'Select level'}
                          </span>
                          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                          </span>
                        </Listbox.Button>
                        <Transition
                          as={React.Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                        >
                          <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none">
                            {availableLevels.map((level) => (
                              <Listbox.Option
                                key={level?.id}
                                value={level}
                                className={({ active }) =>
                                  `relative cursor-default py-2 pr-4 pl-10 select-none ${
                                    active
                                      ? actionListboxOptionActiveClassName
                                      : 'text-gray-900'
                                  }`
                                }
                              >
                                {({ selected }) => (
                                  <>
                                    <span
                                      className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}
                                    >
                                      {level?.name}
                                    </span>
                                    {selected && (
                                      <span
                                        className={`absolute inset-y-0 left-0 flex items-center pl-3 ${actionTextClassName}`}
                                      >
                                        <CheckIcon className="h-5 w-5" />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </Transition>
                      </div>
                    </Listbox>
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={onClose}
                    disabled={isPersisting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={`rounded-md px-4 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50 ${actionPrimaryButtonClassName}`}
                    onClick={onConfirm}
                    disabled={!modalState.classificationLevel || isPersisting}
                  >
                    {isPersisting ? 'Saving...' : isEditing ? 'Update' : 'Add'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ClassificationLevelFormModal;
